import { allPass, always, both, complement, cond, is, isEmpty, isNil, test } from 'ramda'
import { isPlainObject, memoize } from 'utils/misc'
import moize from 'moize'
import jsYaml from 'js-yaml'
import { isNilOrEmpty } from 'utils/fp'
import { Address4, Address6 } from 'ip-address'

export class FieldValidator {
  /**
   * @param validationFn Function
   * @param errorMessage String
   */
  constructor(validationFn, errorMessage) {
    this.validate = validationFn
    this.errorMessage = errorMessage
  }

  withMessage = memoize((message) => new FieldValidator(this.validate, message))
}

// Create a custom inline validator
export const customValidator = (validator, errorMessage) =>
  new FieldValidator(validator, errorMessage)

const fieldIsUnset = (value) => isNil(value) || isEmpty(value) || value === false
export const hasMinLength = (minLen) => (value) =>
  both(is(String), (val) => val.length >= minLen)(value)
export const hasOneLowerChar = both(is(String), test(/[a-z]/))
export const hasOneUpperChar = both(is(String), test(/[A-Z]/))
export const hasOneNumber = both(is(String), test(/[0-9]/))

export const hasOneSpecialChar = both(
  is(String),
  (value) => !/\s/.test(value) && /[^a-zA-Z0-9]/.test(value),
)

export const masterNodesLengthErrorMsg = 'You can only have 1, 3 or 5 master nodes'
export const masterNodeLengthValidator = new FieldValidator(
  (nodes) => fieldIsUnset(nodes) || [1, 3, 5].includes(nodes.length),
  masterNodesLengthErrorMsg,
)

export const cloudProviderValidator = new FieldValidator(
  (name) => fieldIsUnset(name) || !/[^a-z0-9.-]/.test(name),
  'Name is invalid, lower case alphanumeric characters, -, and . only',
)

// Name Validator - All Alphanumeric, Hyphen and Underscore
export const nameWithHyphenAndUnderscoreValidator = new FieldValidator(
  (name) => fieldIsUnset(name) || /^[a-zA-Z0-9-_]+$/.test(name),
  'Name can only contain alphanumeric characters, hypens(-) and underscores(_)',
)

export const namespaceValidator = new FieldValidator(
  (namespace) => fieldIsUnset(namespace) || /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/gi.test(namespace),
  "Namespace is invalid, alphanumeric characters and '-' only",
)

export const emailValidator = new FieldValidator(
  (email) =>
    fieldIsUnset(email) ||
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i.test(
      email,
    ),
  'Email is invalid',
)

export const requiredValidator = new FieldValidator(
  (value) => !fieldIsUnset(value),
  'Field is required',
)

export const requiredValidatorWithMessage = (message) =>
  new FieldValidator((value) => !fieldIsUnset(value), message)

export const matchFieldValidator = memoize(
  (id) =>
    new FieldValidator((value, formFields) => value === formFields[id], 'Fields do not match'),
)

export const lengthValidator = (minLength, maxLength) =>
  new FieldValidator(
    (value) =>
      fieldIsUnset(value) ||
      (value.toString().length >= minLength && value.toString().length <= maxLength),
    `Length must be between ${minLength} and ${maxLength}`,
  )

export const minLengthValidator = memoize(
  (minLength) =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || value.toString().length >= minLength,
      `Length must be greater than ${minLength}`,
    ),
)

export const maxLengthValidator = memoize(
  (maxLength) =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || value.toString().length <= maxLength,
      `Length must be less than ${maxLength}`,
    ),
)

export const nonZeroValidator = memoize(
  () =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || Number(value) > 0,
      `Value must be greater than 0`,
    ),
)

export const nonDecimalValueValidator = memoize(
  () =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || value.toString().indexOf('.') === -1,
      `Value must be a whole number, no decimal points allowed.`,
    ),
)

export const minValueValidator = memoize(
  (min) =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || Number(value) >= min,
      `Value must be greater than or equal to ${min}`,
    ),
)

export const rangeValueValidator = memoize(
  (min, max) =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || (Number(value) >= min && Number(value) <= max),
      `Value must be between ${min} and ${max}`,
    ),
)

export const rangeValueValidatorWithLable = memoize(
  (min, max, minLable, maxLable) =>
    new FieldValidator(
      () => !(min === 0 && max === 0), // Validation fails when both are 0
      min === 0 && max === 0 && `Both ${minLable} or ${maxLable} cannot be 0`,
    ),
)

export const maxValueValidator = memoize(
  (max) =>
    new FieldValidator(
      (value) => fieldIsUnset(value) || Number(value) <= max,
      `Value must be less than or equal to ${max}`,
    ),
)

export const passwordValidator = new FieldValidator(
  (value) =>
    fieldIsUnset(value) ||
    allPass([hasMinLength(8), test(/[a-z]/i), hasOneNumber, hasOneSpecialChar])(value),
  // Show a different error message depending on the validation error
  (value) =>
    cond([
      [complement(hasMinLength(8)), always('Password must be at least 8 characters long')],
      [complement(test(/[a-z]/i)), always('Password must contain at least one letter (any case)')],
      [complement(hasOneNumber), always('Password must contain at least one number')],
      [
        complement(hasOneSpecialChar),
        always(`Password must contain at least one special character (spaces not allowed)`),
      ],
    ])(value),
)

export const colorHexValidator = new FieldValidator(
  (hex) => fieldIsUnset(hex) || /^#[a-f0-9]{6}$/i.test(hex),
  'Color is invalid',
)

export const hexValidator = new FieldValidator(
  (hex) => /^(0x)([a-f0-9]+)$/i.test(hex),
  'Must be a hex value',
)

export const portValidator = new FieldValidator(
  (port) => Number.isInteger(port) && port >= 1 && port <= 65535,
  'Must be a valid port number (1-65535)',
)

export const spotMaxPriceValidator = new FieldValidator(
  (price) => fieldIsUnset(price) || parseFloat(price) > 0.001,
  'Minimum value is 0.001',
)

export const validators = {
  email: emailValidator,
  password: passwordValidator,
  required: requiredValidator,
  matchField: matchFieldValidator,
  length: lengthValidator,
  minLength: minLengthValidator,
  maxLength: maxLengthValidator,
  minValue: minValueValidator,
  maxValue: maxValueValidator,
  rangeValue: rangeValueValidator,
  rangeValueWithLable: rangeValueValidatorWithLable,
}

export const parseValidator = (key, spec) => {
  if (!validators.hasOwnProperty(key)) {
    if (spec instanceof FieldValidator) {
      return spec
    }
    // Custom validator
    if (typeof spec === 'function') {
      return customValidator(spec)
    }
    throw new Error(`Validator with key ${key} does not exist`)
  }
  const validator = validators[key]
  if (spec === true) {
    return validator
  }
  if (isPlainObject(spec)) {
    const { params, message } = spec
    const validatorWithParams = params ? validator(...params) : validator
    if (message) {
      return validatorWithParams.withMessage(message)
    }
    return validatorWithParams
  }
  if (typeof validator === 'function') {
    return validator(...(Array.isArray(spec) ? spec : [spec]))
  }
}

const moizedYamlLoad = moize(jsYaml.load, {
  maxSize: 10,
})

export const yamlValidator = new FieldValidator(
  (yaml) => {
    try {
      if (isNilOrEmpty(yaml)) return true
      const body = moizedYamlLoad(yaml)
      if (typeof body === 'string' || body === undefined || body === null) {
        return false
      }
      return true
    } catch (err) {
      return false
    }
  },
  (yaml) => {
    try {
      moizedYamlLoad(yaml)
    } catch (err) {
      return err?.message ? err.message : 'Provided YAML code is invalid'
    }
    return 'Provided YAML code is invalid'
  },
)

export const jsonValidator = new FieldValidator(
  (json) => {
    try {
      const parseableString = json.replace(/[^\S\r\n]/g, ' ')
      JSON.parse(parseableString)
      return true
    } catch (err) {
      return false
    }
  },
  (json) => {
    try {
      const parseableString = json.replace(/[^\S\r\n]/g, ' ')
      JSON.parse(parseableString)
    } catch (err) {
      return err?.toString() || 'Provided JSON code is invalid'
    }
    return 'Provided JSON code is invalid'
  },
)

export const validIpv4Validator = new FieldValidator((ip) => {
  return fieldIsUnset(ip) || Address4.isValid(ip)
}, 'Invalid IP Address provided')

export const validIpv6Validator = new FieldValidator((ip) => {
  return fieldIsUnset(ip) || Address6.isValid(ip)
}, 'Invalid IP Address provided')

export const nameValidator = memoize(
  (message) =>
    new FieldValidator(
      (name) => /^[a-z]([-a-z0-9]*[a-z0-9])?$/.test(name),
      message || 'Name is invalid',
    ),
)

export const volumeSizeValidator = memoize(
  (volumeSize) =>
    new FieldValidator((size) => {
      const vmSize = Number(size)
      return Number.isInteger(vmSize) && vmSize >= volumeSize
    }, `Volume size must be at least ${volumeSize} GiB`),
)

export const kubernetesNameValidator = new FieldValidator((name) => {
  return !/[^a-z0-9.-]/.test(name)
}, "Name must consist only of lowercase alphanumeric characters, '-', or '.'")

export const validPortValidator = new FieldValidator((port) => {
  const portNumber = Number(port)
  return Number.isInteger(portNumber) && portNumber >= 1 && portNumber <= 65535
}, 'Invalid Port Number provided')

export const validZoneNameValidator = new FieldValidator((zoneName) => {
  if (typeof zoneName !== 'string' || zoneName.trim() === '') {
    return false
  }
  const trimmedZoneName = zoneName.trim()
  if (!trimmedZoneName.endsWith('.')) {
    return false
  }
  const parts = trimmedZoneName.slice(0, -1).split('.')
  return parts.length > 1 && parts.every((part) => /^[a-zA-Z0-9-]+$/.test(part))
}, 'Invalid Zone Name. It must be a fully qualified domain name (FQDN) ending with a dot (.) and contain at least one subdomain.')

export const vnidRangeValidator = memoize(({ vnidRange = '-1:-1', underlayType }) => {
  // 1. Extract and pre-parse range outside the FieldValidator loop for performance
  const [startStr, endStr] = vnidRange?.split(':') ?? []
  const min = Number(startStr)
  const max = Number(endStr)
  const isRangeValid = !Number.isNaN(min) && !Number.isNaN(max) && vnidRange !== '-1:-1'

  return new FieldValidator((value) => {
    if (value == null || !isRangeValid) return false

    // Only run range validation when underlay type is vlan
    if (underlayType !== 'vlan') return true

    // 3. Logic Check: Error if the value falls INSIDE the range
    const numValue = Number(value)
    if (Number.isNaN(numValue)) return false // Explicit NaN check (optional)
    const isInsideRange = numValue >= min && numValue <= max

    return !isInsideRange // Valid only if OUTSIDE the range
  }, `VLAN ID must be outside of the virtual network ID range ${vnidRange}`)
})

export const existingVmOwnerValidator = memoize(
  (ownerId) =>
    new FieldValidator(
      (value) => value !== ownerId,
      'This user already owns the selected VM. Please choose a different user.',
    ),
)
